import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const BroadcastModal = ({ onClose, onBroadcast }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [targetRoles, setTargetRoles] = useState(['staff', 'manager']);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const roleOptions = [
    { value: 'staff', label: 'Staff', description: 'Front desk and support staff' },
    { value: 'trainer', label: 'Trainers', description: 'Personal trainers and instructors' },
    { value: 'manager', label: 'Managers', description: 'Gym managers' },
    { value: 'owner', label: 'Owners', description: 'Gym owners' }
  ];

  const handleRoleToggle = (role) => {
    setTargetRoles(prev => {
      if (prev?.includes(role)) {
        return prev?.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleSend = async () => {
    if (!subject?.trim() || !message?.trim() || targetRoles?.length === 0) {
      setError('Please fill in all fields and select at least one role');
      return;
    }

    setSending(true);
    setError(null);
    const { error: broadcastError } = await onBroadcast(subject, message, targetRoles);
    if (broadcastError) {
      setError(broadcastError?.message || 'Failed to send broadcast');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="Radio" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Broadcast Message</h2>
              <p className="text-sm text-muted-foreground mt-1">Send a message to multiple staff members</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {error && (
            <div className="bg-error/10 border border-error rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <Input
            label="Subject"
            type="text"
            placeholder="Brief subject for the broadcast"
            value={subject}
            onChange={(e) => setSubject(e?.target?.value)}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Message <span className="text-error">*</span>
            </label>
            <textarea
              className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Type your broadcast message here..."
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Target Recipients ({targetRoles?.length} selected)
            </label>
            <div className="space-y-3">
              {roleOptions?.map((role) => {
                const isSelected = targetRoles?.includes(role?.value);
                return (
                  <button
                    key={role?.value}
                    onClick={() => handleRoleToggle(role?.value)}
                    className={`
                      w-full p-4 rounded-lg border-2 transition-all text-left
                      ${isSelected
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                        ${isSelected ? 'border-primary bg-primary' : 'border-border'}
                      `}>
                        {isSelected && <Icon name="Check" size={14} color="white" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground text-sm">{role?.label}</h3>
                        <p className="text-xs text-muted-foreground">{role?.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !subject?.trim() || !message?.trim() || targetRoles?.length === 0}
            iconName="Send"
            iconPosition="right"
          >
            {sending ? 'Sending...' : 'Send Broadcast'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BroadcastModal;